/**
 * One pasted cell as the value the cell editor would have produced - the parser
 * Bulk Add and Add Task read what the operator typed with. (It lived in the old
 * 批量上传 wizard until Alan had that button deleted, 2026-09-30.)
 */

import { fieldOptions } from './container-field-registry.mjs?v=20260806.100';
import { dashboardEditorValue } from './dashboard-cell-interactions.mjs?v=20260806.100';
import { containerStatusLabel } from './container-status-presentation.mjs?v=20260806.100';
import { parseTypedDateTime, parseTypedDay } from './typed-date.mjs?v=20260806.100';

const clean = (value) => String(value ?? '').replace(/ /g, ' ').trim();
const token = (value) => clean(value).toLowerCase().replace(/[\s_-]+/g, '');

// One parser for a typed date and a pasted one (web/typed-date.mjs), under the
// names this module has always exported them by.
export const parseBulkDay = parseTypedDay;
export const parseBulkDateTime = parseTypedDateTime;

// A status is matched by its token, its English label or its Chinese one - and
// the ops workbook writes it with a sort digit in front ("4可提", "6PENDING",
// scripts/build_container_data_import.py OCEAN_STATUS_MAP), so that digit is
// dropped for the status menu. Only there: "40HQ" is a size, not "HQ".
function selectValue(column, raw) {
  const options = fieldOptions(column).filter((option) => option !== 'CUSTOM');
  const status = column.key === 'ocean_status';
  const wanted = new Set([token(raw), status ? token(clean(raw).replace(/^\d+/u, '')) : ''].filter(Boolean));
  for (const option of options) {
    const names = status
      ? [option, containerStatusLabel(option, 'en'), containerStatusLabel(option, 'zh')]
      : [option];
    if (names.some((name) => wanted.has(token(name)))) return option;
  }
  return column.allowCustom ? clean(raw) : null;
}

/**
 * One pasted cell as the value the cell editor would have produced, or why not.
 * `input` is what goes through dashboardEditorValue, so a date becomes the same
 * 12:00 UTC the date picker writes.
 */
export function parseBulkValue(column, raw, { now = Date.now() } = {}) {
  const text = clean(raw);
  if (!text) return { skip: true };
  if (text.includes('\t')) return { error: 'columns' };
  let input = text;
  if (column.editor === 'date') {
    input = column.format === 'date-only' ? parseBulkDay(text, now) : parseBulkDateTime(text, now);
    if (!input) return { error: column.format === 'date-only' ? 'date' : 'date_time' };
  } else if (column.editor === 'number') {
    const numeric = text.replace(/,/g, '').replace(/\s*(lbs?|磅)$/iu, '');
    if (!/^\d+(?:\.\d+)?$/u.test(numeric)) return { error: 'number' };
    input = numeric;
  } else if (column.editor === 'currency') {
    const dollars = text.replace(/[$,\s]/g, '');
    if (!/^\d+(?:\.\d{1,2})?$/u.test(dollars)) return { error: 'number' };
    input = dollars;
  } else if (column.editor === 'select') {
    input = selectValue(column, text);
    if (input === null) return { error: 'option' };
  }
  try {
    return { value: dashboardEditorValue(column, input), shown: input };
  } catch {
    return { error: 'value' };
  }
}
